// @ts-nocheck
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useOrganizationContext } from '@/contexts/OrganizationContext';
import { getNormalizedRegistrationPricing, type MinimalRegistrationForPricing } from '@/utils/orderPricing';

export interface Registration {
  id: string;
  event_id: string;
  order_group_id?: string | null;
  registered_by?: string | null;
  registered_by_name?: string | null;
  registered_by_email?: string | null;
  registration_type: 'individual' | 'team' | 'spectator';
  registrant_data: {
    contact_name?: string;
    contact_email?: string;
    contact_phone?: string;
    pricing_tier?: string;
    division?: string;
    extras_selected?: Array<{
      name: string;
      quantity: number;
      unit_price: number;
      total: number;
    }>;
    spectator_tickets?: Array<{
      name: string;
      quantity: number;
      unit_price: number;
      total: number;
    }>;
    special_requests?: {
      requested: boolean;
      details: string;
      fee: number;
    };
    discount_code?: string;
  };
  pricing_breakdown: {
    base_price: number;
    extras_total?: number;
    tickets_total?: number;
    special_requests_fee?: number;
    discount_amount?: number;
    total: number;
  };
  status: 'checkout_pending' | 'pending' | 'confirmed' | 'cancelled' | 'waitlisted';
  payment_status: 'pending' | 'paid' | 'failed' | 'refunded';
  payment_id?: string;
  created_at: string;
  payment_amount?: number;
  payment_method?: string;
  stripe_payment_intent_id?: string;
  synced_to_sheets?: boolean;
  // Dedicated financial columns (stored in dollars)
  registration_amount?: number;
  extras_amount?: number;
  special_request_amount?: number;
  tickets_amount?: number;
  // Order linking info
  linked_registrations_count?: number;
}

export interface RegistrationStats {
  total: number;
  confirmed: number;
  pending: number;
  cancelled: number;
  revenue: number;
  collectedRevenue: number;
  pendingRevenue: number;
  discounts: number;
  comped: number;
  // Category breakdowns
  registrationsCollected: number;
  registrationsPending: number;
  extrasCollected: number;
  extrasPending: number;
  specialRequestsCollected: number;
  specialRequestsPending: number;
  ticketsCollected: number;
  ticketsPending: number;
}

export function useRegistrations(eventId: string) {
  const { organization } = useOrganizationContext();
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<RegistrationStats>({
    total: 0,
    confirmed: 0,
    pending: 0,
    cancelled: 0,
    revenue: 0,
    collectedRevenue: 0,
    pendingRevenue: 0,
    discounts: 0,
    comped: 0,
    registrationsCollected: 0,
    registrationsPending: 0,
    extrasCollected: 0,
    extrasPending: 0,
    specialRequestsCollected: 0,
    specialRequestsPending: 0,
    ticketsCollected: 0,
    ticketsPending: 0
  });

  const fetchRegistrations = async () => {
    console.log('[useRegistrations] Fetching for eventId:', eventId, 'organization:', organization?.id);
    
    if (!eventId || !organization?.id) {
      console.log('[useRegistrations] Missing eventId or organization.id, returning early');
      return;
    }

    try {
      setLoading(true);

      // Query NEW registrations table 
      // IMPORTANT: Exclude 'checkout_pending' status - these are incomplete Stripe checkouts
      const { data: newRegs, error: newError } = await supabase
        .from('registrations')
        .select('*')
        .eq('event_id', eventId)
        .eq('organization_id', organization.id)
        .neq('status', 'checkout_pending')
        .order('created_at', { ascending: false });

      console.log('[useRegistrations] Query result - newRegs:', newRegs?.length, 'error:', newError);

      if (newError) throw newError;
      
      // Fetch profile names for registered_by users
      const registeredByIds = [...new Set((newRegs || []).map(r => (r as any).registered_by).filter(Boolean))];
      let profilesMap: Record<string, { name: string; email: string }> = {};
      
      if (registeredByIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, name, email')
          .in('id', registeredByIds);
        
        if (profiles) {
          profilesMap = profiles.reduce((acc, p) => {
            acc[p.id] = { name: p.name || '', email: p.email || '' };
            return acc;
          }, {} as Record<string, { name: string; email: string }>);
        }
      }
      
      // Count registrations per order_group_id for linking info
      const orderGroupCounts: Record<string, number> = {};
      for (const reg of (newRegs || [])) {
        const groupId = (reg as any).order_group_id;
        if (groupId) {
          orderGroupCounts[groupId] = (orderGroupCounts[groupId] || 0) + 1;
        }
      }

      // OLD event_registrations table is deprecated - no longer querying it
      // All new registrations go to the registrations table only
      const oldRegs: any[] = [];

      // Transform NEW registrations - extract contact info from nested structure
      const transformedNewData = (newRegs || []).map(reg => {
        const rawData = reg.registrant_data || {};
        const regAny = reg as any;
        
        // Extract contact info from nested structures (coach, team, athlete)
        const contactName = rawData.coach?.name || rawData.athlete?.name || rawData.team?.name || rawData.contact_name || 'Unknown';
        const contactEmail = rawData.coach?.email || rawData.athlete?.email || rawData.contact_email || '';
        const contactPhone = rawData.coach?.phone || rawData.athlete?.phone || rawData.contact_phone || '';
        const division = rawData.team?.division_id || rawData.division || '';
        
        // Get registered_by user info from profilesMap
        const registeredById = regAny.registered_by;
        const registeredByProfile = registeredById ? profilesMap[registeredById] : null;
        const registeredByName = registeredByProfile 
          ? registeredByProfile.name || null
          : null;
        const registeredByEmail = registeredByProfile?.email || null;
        
        // Get linked registrations count
        const orderGroupId = regAny.order_group_id ?? null;
        const linkedCount = orderGroupId ? orderGroupCounts[orderGroupId] : 0;
        
        return {
          ...reg,
          registrant_data: {
            ...rawData,
            contact_name: contactName,
            contact_email: contactEmail,
            contact_phone: contactPhone,
            division: division,
          },
          order_group_id: orderGroupId,
          registered_by: regAny.registered_by ?? null,
          registered_by_name: registeredByName,
          registered_by_email: registeredByEmail,
          linked_registrations_count: linkedCount,
          payment_id: reg.payment_id || null,
          payment_amount: reg.pricing_breakdown?.total || 0,
          payment_status: reg.payment_status || 'pending',
          payment_method: null,
          stripe_payment_intent_id: reg.stripe_session_id,
          synced_to_sheets: reg.synced_to_sheets || false,
          // Include dedicated financial columns (stored in dollars)
          registration_amount: reg.registration_amount,
          extras_amount: reg.extras_amount,
          special_request_amount: reg.special_request_amount,
          tickets_amount: reg.tickets_amount
        };
      });

      // Transform OLD registrations to match new format - include dedicated financial columns
      const transformedOldData = (oldRegs || []).map(reg => {
        const regData = reg.registration_data || {};
        const formResponses = regData.formResponses || {};
        
        // Extract contact info from first form response
        const firstForm = Object.values(formResponses)[0] as any || {};
        
        return {
          id: reg.id,
          event_id: reg.event_id,
          registration_type: reg.registration_type,
          registrant_data: {
            contact_name: reg.name,
            contact_email: reg.email,
            contact_phone: firstForm['1750512150526'] || '',
            pricing_tier: regData.selectedItems?.[0]?.name || '',
            division: regData.selectedItems?.[0]?.name || '',
            extras_selected: regData.extras || [],
            spectator_tickets: regData.spectatorTickets || [],
          },
          pricing_breakdown: {
            base_price: regData.subtotal || 0,
            extras_total: regData.extras?.reduce((sum: number, e: any) => sum + (e.price * e.quantity), 0) || 0,
            tickets_total: regData.spectatorTickets?.reduce((sum: number, t: any) => sum + (t.price * t.quantity), 0) || 0,
            discount_amount: (regData.subtotal || 0) - (regData.finalTotal || 0),
            total: regData.finalTotal || reg.total_amount_cents || 0,
          },
          status: reg.status,
          payment_status: reg.stripe_session_id ? 'pending' : 'pending',
          payment_id: null,
          created_at: reg.created_at,
          payment_amount: reg.total_amount_cents,
          payment_method: null,
          stripe_payment_intent_id: reg.stripe_session_id,
          synced_to_sheets: true,
          // Include dedicated financial columns from event_registrations (already in dollars)
          registration_amount: (reg as any).registration_amount,
          extras_amount: (reg as any).extras_amount,
          special_request_amount: (reg as any).special_request_amount,
          tickets_amount: (reg as any).tickets_amount
        };
      });

      // Combine both datasets
      const allRegistrations = [...transformedNewData, ...transformedOldData];
      setRegistrations(allRegistrations);

      // Calculate normalized totals (handles multi-team orders that store order-level amounts)
      const byOrderGroup: Record<string, Registration[]> = {};
      for (const r of allRegistrations) {
        const key = (r as any).order_group_id;
        if (!key) continue;
        if (!byOrderGroup[key]) byOrderGroup[key] = [];
        byOrderGroup[key].push(r);
      }

      const normalized: Record<
        string,
        {
          totalCents: number;
          regAmountCents: number;
          discountCents: number;
          extrasCents: number;
          ticketsCents: number;
          specialCents: number;
        }
      > = {};

      const getExtrasCents = (r: Registration) => {
        if (typeof r.extras_amount === 'number') return Math.round(r.extras_amount * 100);
        return r.pricing_breakdown?.extras_total ?? 0;
      };
      const getTicketsCents = (r: Registration) => {
        if (typeof r.tickets_amount === 'number') return Math.round(r.tickets_amount * 100);
        return r.pricing_breakdown?.tickets_total ?? 0;
      };
      const getSpecialCents = (r: Registration) => {
        if (typeof r.special_request_amount === 'number') return Math.round(r.special_request_amount * 100);
        return (r.pricing_breakdown as any)?.special_requests_fee ?? 0;
      };

      // Default (non-group) values
      for (const r of allRegistrations) {
        const regAmountCents = typeof r.registration_amount === 'number'
          ? Math.round(r.registration_amount * 100)
          : r.pricing_breakdown?.base_price ?? 0;

        const discountCents =
          (r.pricing_breakdown as any)?.discount_amount ?? (r.pricing_breakdown as any)?.discount ?? 0;

        const extrasCents = getExtrasCents(r);
        const ticketsCents = getTicketsCents(r);
        const specialCents = getSpecialCents(r);

        const totalCents = regAmountCents + extrasCents + ticketsCents + specialCents;

        normalized[r.id] = { totalCents, regAmountCents, discountCents, extrasCents, ticketsCents, specialCents };
      }

      // Override grouped registrations with normalized per-team base/discount
      for (const group of Object.values(byOrderGroup)) {
        const pricingGroup: MinimalRegistrationForPricing[] = group.map((r) => ({
          id: r.id,
          order_group_id: r.order_group_id ?? null,
          registration_amount: r.registration_amount ?? null,
          pricing_breakdown: (r.pricing_breakdown as any) ?? null,
        }));

        for (const r of group) {
          const { regAmountCents, discountCents } = getNormalizedRegistrationPricing(
            {
              id: r.id,
              order_group_id: r.order_group_id ?? null,
              registration_amount: r.registration_amount ?? null,
              pricing_breakdown: (r.pricing_breakdown as any) ?? null,
            },
            pricingGroup
          );

          const extrasCents = getExtrasCents(r);
          const ticketsCents = getTicketsCents(r);
          const specialCents = getSpecialCents(r);
          const totalCents = regAmountCents + extrasCents + ticketsCents + specialCents;

          normalized[r.id] = { totalCents, regAmountCents, discountCents, extrasCents, ticketsCents, specialCents };
        }
      }

      // Status counts
      const confirmed = allRegistrations.filter((r) => r.status === 'confirmed').length;
      const pending = allRegistrations.filter((r) => r.status === 'pending').length;
      const cancelled = allRegistrations.filter((r) => r.status === 'cancelled').length;

      // Revenue totals
      const collectedRevenue = allRegistrations
        .filter((r) => r.payment_status === 'paid')
        .reduce((sum, r) => sum + (normalized[r.id]?.totalCents ?? 0), 0);

      const pendingRevenue = allRegistrations
        .filter((r) => r.payment_status !== 'paid' && r.status !== 'cancelled')
        .reduce((sum, r) => sum + (normalized[r.id]?.totalCents ?? 0), 0);

      const discounts = allRegistrations.reduce((sum, r) => sum + (normalized[r.id]?.discountCents ?? 0), 0);

      const comped = allRegistrations.filter((r) => (normalized[r.id]?.totalCents ?? 0) === 0 && r.status !== 'cancelled').length;

      const revenue = collectedRevenue + pendingRevenue;

      // Category breakdowns (cents)
      const paidRegs = allRegistrations.filter((r) => r.payment_status === 'paid' && r.status !== 'cancelled');
      const unpaidRegs = allRegistrations.filter((r) => r.payment_status !== 'paid' && r.status !== 'cancelled');

      const registrationsCollected = paidRegs.reduce((sum, r) => sum + (normalized[r.id]?.regAmountCents ?? 0), 0);
      const registrationsPending = unpaidRegs.reduce((sum, r) => sum + (normalized[r.id]?.regAmountCents ?? 0), 0);

      const extrasCollected = paidRegs.reduce((sum, r) => sum + (normalized[r.id]?.extrasCents ?? 0), 0);
      const extrasPending = unpaidRegs.reduce((sum, r) => sum + (normalized[r.id]?.extrasCents ?? 0), 0);

      const specialRequestsCollected = paidRegs.reduce((sum, r) => sum + (normalized[r.id]?.specialCents ?? 0), 0);
      const specialRequestsPending = unpaidRegs.reduce((sum, r) => sum + (normalized[r.id]?.specialCents ?? 0), 0);

      const ticketsCollected = paidRegs.reduce((sum, r) => sum + (normalized[r.id]?.ticketsCents ?? 0), 0);
      const ticketsPending = unpaidRegs.reduce((sum, r) => sum + (normalized[r.id]?.ticketsCents ?? 0), 0);

      setStats({
        total: allRegistrations.length,
        confirmed,
        pending,
        cancelled,
        revenue,
        collectedRevenue,
        pendingRevenue,
        discounts,
        comped,
        registrationsCollected,
        registrationsPending,
        extrasCollected,
        extrasPending,
        specialRequestsCollected,
        specialRequestsPending,
        ticketsCollected,
        ticketsPending,
      });

    } catch (error) {
      console.error('Error fetching registrations:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRegistrations();

    // Subscribe to real-time updates
    const channel = supabase
      .channel(`registrations-${eventId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'registrations',
          filter: `event_id=eq.${eventId}`
        },
        () => {
          fetchRegistrations();
        }
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, [eventId, organization?.id]);

  return {
    registrations,
    loading,
    stats,
    refetch: fetchRegistrations
  };
}
